<template>
  <div class="gm-list-pane">
    <div class="gm-toolbar">
      <div class="gm-toolbar__left">
        <div class="gm-toolbar-check" v-b-tooltip.hover :title="$t('inbox.selectAll')" @click="$emit('toggle-select-all')">
          <svg class="gm-custom-check" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="14" height="14" rx="3" class="gm-check-box" />
            <line v-if="someSelected && !allSelected" x1="4" y1="8" x2="12" y2="8" class="gm-check-dash" />
            <polyline v-if="allSelected" points="3.5,8 6.5,11 12.5,5" class="gm-check-mark" />
          </svg>
        </div>
        <transition name="gm-fade">
          <span v-if="selectedCount > 0" class="gm-toolbar-count">{{ selectedCount }}</span>
        </transition>
        <div class="gm-toolbar-sep"></div>
        <button
          class="gm-toolbar-btn gm-toolbar-btn--labeled"
          :disabled="selectedCount === 0"
          @click="$emit('bulk-mark-read', true)"
          v-b-tooltip.hover :title="$t('inbox.markAsRead')"
        ><b-icon icon="envelope-open" /><span class="gm-toolbar-btn__label">{{ $t('inbox.markAsRead') }}</span></button>
        <button
          class="gm-toolbar-btn gm-toolbar-btn--labeled"
          :disabled="selectedCount === 0"
          @click="$emit('bulk-mark-read', false)"
          v-b-tooltip.hover :title="$t('inbox.markAsUnread')"
        ><b-icon icon="envelope" /><span class="gm-toolbar-btn__label">{{ $t('inbox.markAsUnread') }}</span></button>
        <button
          v-if="canDelete"
          class="gm-toolbar-btn gm-toolbar-btn--labeled gm-toolbar-btn--danger"
          :disabled="selectedCount === 0"
          @click="$emit('bulk-delete')"
          v-b-tooltip.hover :title="$t('inbox.delete')"
        ><b-icon icon="trash" /><span class="gm-toolbar-btn__label">{{ $t('inbox.delete') }}</span></button>

        <div class="gm-more-wrap" v-click-outside="closeMoreMenu">
          <button
            class="gm-toolbar-btn gm-toolbar-btn--labeled"
            :disabled="selectedCount === 0"
            @click.stop="selectedCount > 0 && (showMoreMenu = !showMoreMenu)"
            v-b-tooltip.hover :title="$t('inbox.moreActions')"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><circle cx="2.5" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="13.5" cy="8" r="1.5"/></svg>
          </button>
          <transition name="gm-drop">
            <div v-if="showMoreMenu" class="gm-more-menu" @click.stop>
              <div class="gm-more-menu__item" @click="$emit('bulk-star', true); showMoreMenu = false">
                <b-icon icon="star-fill" class="gm-more-menu__icon" style="color:#f4b400" /> {{ $t('inbox.starSelected') }}
              </div>
              <div class="gm-more-menu__item" @click="$emit('bulk-star', false); showMoreMenu = false">
                <b-icon icon="star" class="gm-more-menu__icon" /> {{ $t('inbox.unstarSelected') }}
              </div>
              <div class="gm-more-menu__divider" />
              <div class="gm-more-menu__item gm-more-menu__item--sub" @mouseenter="showMoveMenu=true" @mouseleave="showMoveMenu=false">
                <b-icon icon="folder-symlink" class="gm-more-menu__icon" /> {{ $t('inbox.moveTo') }}
                <b-icon icon="chevron-right" class="gm-more-menu__chevron" />
                <div v-show="showMoveMenu" class="gm-more-submenu">
                  <div
                    v-for="f in moveTargetFolders"
                    :key="f.key"
                    class="gm-more-menu__item"
                    @click="$emit('bulk-move-to', f.key); showMoreMenu = false; showMoveMenu = false"
                  >
                    <b-icon :icon="f.icon" class="gm-more-menu__icon" /> {{ f.label }}
                  </div>
                  <template v-if="imapFolders.length">
                    <div class="gm-more-menu__divider" />
                    <div
                      v-for="f in imapFolders"
                      :key="f.name"
                      class="gm-more-menu__item"
                      @click="$emit('bulk-move-to', f.name); showMoreMenu = false; showMoveMenu = false"
                    >
                      <b-icon icon="folder" class="gm-more-menu__icon" /> {{ f.name }}
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="currentFolder === 'Spam'" class="gm-more-menu__item" @click="$emit('bulk-move-to', 'INBOX'); showMoreMenu = false">
                <b-icon icon="shield-check" class="gm-more-menu__icon" style="color:#22c55e" /> {{ $t('inbox.unmarkAsSpam') }}
              </div>
              <div v-else-if="currentFolder === 'Trash'" class="gm-more-menu__item" @click="$emit('bulk-move-to', 'INBOX'); showMoreMenu = false">
                <b-icon icon="arrow-counterclockwise" class="gm-more-menu__icon" style="color:#60a5fa" /> {{ $t('inbox.restore') }}
              </div>
              <div v-else class="gm-more-menu__item" @click="$emit('bulk-move-to-spam'); showMoreMenu = false">
                <b-icon icon="exclamation-octagon" class="gm-more-menu__icon" /> {{ $t('inbox.markAsSpam') }}
              </div>
              <div class="gm-more-menu__divider" />
              <div class="gm-more-menu__item" @click="$emit('deselect-all'); showMoreMenu = false">
                <b-icon icon="x-circle" class="gm-more-menu__icon" /> {{ $t('inbox.deselectAll') }}
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div class="gm-toolbar__right">
        <span v-if="totalEmails > 0" class="gm-toolbar-info">{{ totalEmails }} messages</span>
        <select class="gm-page-size-select" :value="pageSize" @change="$emit('page-size-change', $event.target.value)" title="Emails per page">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <button class="gm-toolbar-btn" :disabled="currentPage===1" @click="$emit('prev-page')"><b-icon icon="chevron-left" /></button>
        <span class="gm-toolbar-info" style="padding:0 0.125rem">{{ currentPage }}/{{ totalPages }}</span>
        <button class="gm-toolbar-btn" :disabled="currentPage>=totalPages" @click="$emit('next-page')"><b-icon icon="chevron-right" /></button>
        <button class="gm-toolbar-btn" @click="$emit('refresh')" :disabled="loading" v-b-tooltip.hover title="Refresh">
          <b-spinner small v-if="loading" style="width:12px;height:12px" />
          <b-icon v-else icon="arrow-clockwise" />
        </button>
      </div>
    </div>

    <b-alert variant="danger" dismissible :show="!!emailsError" @dismissed="$emit('dismiss-error', 'emails')" class="m-2 mb-0 py-2">{{ emailsError }}</b-alert>
    <b-alert variant="danger" dismissible :show="!!accountError" @dismissed="$emit('dismiss-error', 'account')" class="m-2 mb-0 py-2">{{ accountError }}</b-alert>

    <div v-if="loading" class="gm-list-scroll">
      <div v-for="n in 12" :key="n" class="gm-row gm-row--skeleton">
        <div style="width:40px;display:flex;align-items:center;justify-content:center"><b-skeleton width="14px" height="14px" style="border-radius:3px" /></div>
        <div style="width:32px"></div>
        <b-skeleton width="120px" style="flex-shrink:0" />
        <b-skeleton width="55%" />
        <b-skeleton width="46px" style="flex-shrink:0" />
      </div>
    </div>

    <div v-else-if="!hasAccount" class="gm-empty">
      <b-icon icon="envelope" class="gm-empty__icon" />
      <p class="gm-empty__title">{{ $t('inbox.noAccountTitle') }}</p>
      <p class="gm-empty__sub">{{ $t('inbox.noAccountSub') }}</p>
      <button class="gm-compose-btn mt-3" style="margin:0 auto" @click="$emit('add-account')">
        <b-icon icon="plus" class="mr-1" /> {{ $t('inbox.addAccount') }}
      </button>
    </div>

    <div v-else-if="emails.length === 0" class="gm-empty">
      <b-icon icon="inbox" class="gm-empty__icon" />
      <p class="gm-empty__title">{{ $t('inbox.noMessages') }}</p>
      <p class="gm-empty__sub">{{ $t('inbox.isEmpty', { folder: currentFolder }) }}</p>
    </div>

    <div v-else class="gm-list-scroll">
      <div
        v-for="email in emails"
        :key="email.id || email.message_id"
        class="gm-row"
        :class="{
          'gm-row--unread': !email.is_read,
          'gm-row--selected': isSelected(email.id),
          'gm-row--active': isActive(email.id)
        }"
        @click="$emit('email-click', email)"
      >
        <div class="gm-row__check" @click.stop="$emit('toggle-select', email.id)">
          <svg class="gm-custom-check" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="14" height="14" rx="3" class="gm-check-box" />
            <polyline v-if="isSelected(email.id)" points="3.5,8 6.5,11 12.5,5" class="gm-check-mark" />
          </svg>
        </div>
        <div class="gm-row__star" :class="{ 'gm-row__star--flagged': email.is_flagged }" @click.stop="$emit('toggle-star', email)" v-b-tooltip.hover :title="email.is_flagged ? $t('inbox.unstar') : $t('inbox.star')">
          <b-icon :icon="email.is_flagged ? 'star-fill' : 'star'" :class="email.is_flagged ? 'gm-star--on' : 'gm-star--off'" />
        </div>
        <div class="gm-row__from">
          <div class="gm-row__avatar">{{ senderInitial(email) }}</div>
          <span class="gm-row__from-name">{{ email.from && (email.from.name || email.from.address) || '—' }}</span>
          <span v-if="threadCountMap[email.id]" class="gm-row__thread-count">{{ threadCountMap[email.id] }}</span>
        </div>
        <div class="gm-row__body">
          <span class="gm-row__subject">{{ email.subject || '(no subject)' }}</span>
          <span class="gm-row__preview"> — {{ emailPreview(email) }}</span>
        </div>
        <div class="gm-row__end">
          <span class="gm-row__date">{{ formatDate(email.received_at) }}</span>
          <div class="gm-row__actions">
            <button v-if="canSend" class="gm-row__action" @click.stop="$emit('quick-reply', email)" v-b-tooltip.hover :title="$t('inbox.reply')">
              <b-icon icon="reply" />
            </button>
            <button v-if="canSend" class="gm-row__action" @click.stop="$emit('quick-forward', email)" v-b-tooltip.hover :title="$t('inbox.forward')">
              <b-icon icon="forward" />
            </button>
            <button class="gm-row__action" @click.stop="$emit('quick-toggle-read', email)" v-b-tooltip.hover :title="email.is_read ? $t('inbox.markAsUnread') : $t('inbox.markAsRead')">
              <b-icon :icon="email.is_read ? 'envelope' : 'envelope-open'" />
            </button>
            <button v-if="canDelete" class="gm-row__action gm-row__action--danger" @click.stop="$emit('quick-delete', email)" v-b-tooltip.hover :title="$t('inbox.delete')">
              <b-icon icon="trash" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InboxEmailList',
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutsideHandler);
      },
      unbind(el) { document.removeEventListener('click', el._clickOutsideHandler); },
    },
  },
  props: {
    emails: { type: Array, required: true },
    threads: { type: Array, default: () => [] },
    selectedEmailIds: { type: Set, required: true },
    activeEmailId: { type: [String, Number], default: null },
    loading: { type: Boolean, default: false },
    hasAccount: { type: Boolean, default: false },
    currentFolder: { type: String, required: true },
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true },
    totalEmails: { type: Number, required: true },
    pageSize: { type: Number, required: true },
    emailsError: { type: String, default: null },
    accountError: { type: String, default: null },
    allSelected: { type: Boolean, default: false },
    someSelected: { type: Boolean, default: false },
    moveTargetFolders: { type: Array, default: () => [] },
    imapFolders: { type: Array, default: () => [] },
    canSend: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: true }
  },
  data() {
    return {
      showMoreMenu: false,
      showMoveMenu: false
    };
  },
  computed: {
    selectedCount() {
      return this.selectedEmailIds.size;
    },
    threadCountMap() {
      const map = {};
      for (const t of this.threads) {
        if (t.count > 1) {
          for (const m of t.messages) {
            map[m.id] = t.count;
          }
        }
      }
      return map;
    }
  },
  methods: {
    isSelected(id) {
      return this.selectedEmailIds.has(id);
    },
    isActive(id) {
      return this.activeEmailId === id;
    },
    closeMoreMenu() {
      this.showMoreMenu = false;
      this.showMoveMenu = false;
    },
    senderInitial(email) {
      const name = (email.from && (email.from.name || email.from.address)) || '?';
      return name.trim()[0] || '?';
    },
    emailPreview(email) {
      const text = email.body || email.html_body || '';
      return text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim().substring(0, 120);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'now';
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      
      const month = d.toLocaleDateString('en-US', { month: 'short' });
      const day = d.getDate();
      if (d.getFullYear() === now.getFullYear()) return `${month} ${day}`;
      return `${month} ${day}, ${d.getFullYear()}`;
    }
  }
};
</script>

<style scoped>
.gm-list-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--bg-primary, #16171d);
}

.gm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
  background: var(--bg-secondary, #1e1f26);
  flex-shrink: 0;
}

.gm-toolbar__left, .gm-toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.gm-toolbar-check {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.12s;
}
.gm-toolbar-check:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
}

.gm-custom-check {
  width: 16px;
  height: 16px;
}

.gm-check-box {
  fill: none;
  stroke: var(--text-muted, #6b7280);
  stroke-width: 1.5;
}

.gm-check-dash {
  stroke: var(--primary-start, #6c80ff);
  stroke-width: 2;
  stroke-linecap: round;
}

.gm-check-mark {
  fill: none;
  stroke: var(--primary-start, #6c80ff);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.gm-toolbar-count {
  font-size: 0.8125rem;
  color: var(--text-primary, #e5e7eb);
  font-weight: 500;
  padding: 0 0.25rem;
}

.gm-toolbar-sep {
  width: 1px;
  height: 20px;
  background: var(--border-separator, #2a2d35);
  margin: 0 0.25rem;
}

.gm-toolbar-btn {
  background: transparent;
  border: none;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  transition: background 0.12s, color 0.12s;
}
.gm-toolbar-btn:hover:not(:disabled) {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}
.gm-toolbar-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.gm-toolbar-btn--labeled .gm-toolbar-btn__label {
  display: none;
}
@media (min-width: 1200px) {
  .gm-toolbar-btn--labeled .gm-toolbar-btn__label {
    display: inline;
  }
}
.gm-toolbar-btn--danger:hover:not(:disabled) {
  color: #ef4444;
}

.gm-toolbar-info {
  font-size: 0.8125rem;
  color: var(--text-muted, #6b7280);
  padding: 0 0.375rem;
}

.gm-page-size-select {
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  color: var(--text-secondary, #9ca3af);
  border-radius: 4px;
  padding: 0.25rem 0.375rem;
  font-size: 0.8125rem;
  cursor: pointer;
}

.gm-fade-enter-active, .gm-fade-leave-active { transition: opacity 0.15s, transform 0.15s; }
.gm-fade-enter, .gm-fade-leave-to { opacity: 0; transform: scale(0.7); }

.gm-list-scroll {
  flex: 1;
  overflow-y: auto;
}

.gm-row {
  display: grid;
  grid-template-columns: 36px 28px 1fr 2fr 66px;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
  cursor: pointer;
  transition: background 0.08s;
}
.gm-row:hover {
  background: var(--hover-bg, rgba(255,255,255,0.02));
}
.gm-row--unread {
  background: var(--unread-bg, rgba(102,126,234,0.03));
}
.gm-row--unread .gm-row__from-name, .gm-row--unread .gm-row__subject {
  font-weight: 600;
}
.gm-row--selected {
  background: var(--selected-bg, rgba(102,126,234,0.08));
}
.gm-row--active {
  background: var(--active-bg, rgba(102,126,234,0.12));
}
.gm-row--skeleton {
  pointer-events: none;
}

.gm-row__check {
  display: flex;
  align-items: center;
  justify-content: center;
}

.gm-row__star {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s;
}
.gm-row:hover .gm-row__star {
  opacity: 1;
}
.gm-row__star--flagged {
  opacity: 1;
}
.gm-star--on {
  color: #f4b400;
}
.gm-star--off {
  color: var(--text-muted, #6b7280);
}

.gm-row__from {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.gm-row__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  text-transform: uppercase;
}

.gm-row__from-name {
  font-size: 0.875rem;
  color: var(--text-primary, #e5e7eb);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-row__thread-count {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--text-muted, #6b7280);
  background: var(--bg-tertiary, rgba(255,255,255,0.06));
  border-radius: 4px;
  padding: 0 4px;
  min-width: 16px;
  text-align: center;
  line-height: 16px;
  height: 16px;
  flex-shrink: 0;
}

.gm-row__body {
  min-width: 0;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-row__subject {
  color: var(--text-primary, #e5e7eb);
}

.gm-row__preview {
  color: var(--text-muted, #6b7280);
}

.gm-row__end {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  position: relative;
}

.gm-row__date {
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
  text-align: right;
  white-space: nowrap;
}

.gm-row__actions {
  display: none;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  align-items: center;
  gap: 0.125rem;
  background: var(--bg-secondary, #1e1f26);
  border-radius: 6px;
  padding: 0 0.125rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.25);
}

.gm-row:hover .gm-row__actions {
  display: flex;
}

.gm-row:hover .gm-row__date {
  visibility: hidden;
}

.gm-row__action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.1s, color 0.1s;
  padding: 0;
}

.gm-row__action:hover {
  background: var(--hover-bg, rgba(255,255,255,0.08));
  color: var(--text-primary, #e5e7eb);
}

.gm-row__action--danger:hover {
  color: #ef4444;
  background: rgba(239,68,68,0.1);
}

.gm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  text-align: center;
  flex: 1;
}
.gm-empty__icon { font-size: 3.5rem; color: var(--text-muted, #6b7280); margin-bottom: 1rem; }
.gm-empty__title { font-size: 1rem; font-weight: 600; color: var(--text-primary, #e5e7eb); margin: 0 0 0.25rem; }
.gm-empty__sub { font-size: 0.875rem; color: var(--text-muted, #6b7280); margin: 0; }

.gm-compose-btn {
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s, box-shadow 0.12s;
}

.gm-more-wrap {
  position: relative;
  flex-shrink: 0;
}

.gm-drop-enter-active, .gm-drop-leave-active { transition: opacity 0.15s, transform 0.15s; }
.gm-drop-enter, .gm-drop-leave-to { opacity: 0; transform: translateY(-8px); }

.gm-more-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: var(--bg-secondary, #1e1f26);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  min-width: 180px;
  z-index: 100;
  padding: 0.25rem 0;
}

.gm-more-menu__item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: var(--text-primary, #e5e7eb);
  cursor: pointer;
  transition: background 0.12s;
  position: relative;
}
.gm-more-menu__item:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
}
.gm-more-menu__item--sub {
  justify-content: space-between;
}

.gm-more-menu__icon {
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.gm-more-menu__chevron {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--text-muted, #6b7280);
}

.gm-more-menu__divider {
  height: 1px;
  background: var(--border-separator, #2a2d35);
  margin: 0.25rem 0;
}

.gm-more-submenu {
  position: absolute;
  left: 100%;
  top: -0.25rem;
  background: var(--bg-secondary, #1e1f26);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  min-width: 160px;
  padding: 0.25rem 0;
  margin-left: 4px;
}
</style>
